import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/services/api/client";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import { QUERY_KEYS } from "@/services/api/queryKeys";
import { MUTATION_KEYS } from "@/services/api/mutationKeys";
import type { Portal, UpdatePortalPayload } from "@/types";

export const usePortalDetails = (portalId?: string, initialData?: Portal | null) => {
  const queryClient = useQueryClient();

  const portalQuery = useQuery({
    queryKey: portalId
      ? QUERY_KEYS.PORTALS.DETAIL(portalId)
      : ["portals", "detail", "missing"],
    queryFn: async () => {
      if (!portalId) throw new Error("Portal ID is required");
      const response = await apiClient.get<Portal>(
        API_ENDPOINTS.SYSTEM.PORTALS.DETAIL(portalId)
      );
      return response.data;
    },
    enabled: !!portalId,
    initialData: initialData ?? undefined,
    refetchOnMount: "always",
    staleTime: 0,
    // Auto-poll every 3s while portal is still provisioning
    refetchInterval: (query) => {
      const portal = query.state.data;
      return portal?.status === "provisioning" ? 3_000 : false;
    },
  });

  // Single consolidated update mutation for all portal fields
  const updatePortalMutation = useMutation({
    mutationKey: [MUTATION_KEYS.UPDATE_PORTAL],
    mutationFn: async (params: { id: string; payload: UpdatePortalPayload }) => {
      const response = await apiClient.patch<Portal>(
        API_ENDPOINTS.SYSTEM.PORTALS.DETAIL(params.id),
        params.payload
      );
      return response.data;
    },
    onSuccess: (updatedPortal) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTALS.ALL });
      queryClient.setQueryData(
        QUERY_KEYS.PORTALS.DETAIL(updatedPortal.id),
        updatedPortal
      );
    },
  });

  // Retry provisioning mutation
  const retryProvisioningMutation = useMutation({
    mutationKey: [MUTATION_KEYS.RETRY_PROVISIONING],
    mutationFn: async (id: string) => {
      const response = await apiClient.post(
        API_ENDPOINTS.SYSTEM.PORTALS.RETRY_PROVISIONING(id)
      );
      return response.data;
    },
    onSuccess: () => {
      if (portalId) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.PORTALS.DETAIL(portalId),
        });
      }
    },
  });

  return {
    portal: portalQuery.data ?? null,
    isLoading: portalQuery.isLoading,
    error: portalQuery.error,
    updatePortal: (
      id: string,
      payload: UpdatePortalPayload,
      options?: Parameters<typeof updatePortalMutation.mutate>[1]
    ) => updatePortalMutation.mutate({ id, payload }, options),
    isUpdating: updatePortalMutation.isPending,
    retryProvisioning: (id: string) => retryProvisioningMutation.mutate(id),
    isRetrying: retryProvisioningMutation.isPending,
  };
};
